import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { AuthContext } from '../App';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const RedOracionIBA = () => {
  const { user } = useContext(AuthContext);
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOrando, setModalOrando] = useState(null);
  const [usuariosOrando, setUsuariosOrando] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Modal "Estoy Orando" con mensaje opcional
  const [showOrandoModal, setShowOrandoModal] = useState(false);
  const [pedidoOrando, setPedidoOrando] = useState(null);
  const [mensajeOracion, setMensajeOracion] = useState('');
  const [enviandoOracion, setEnviandoOracion] = useState(false);

  // Modal "Nuevo Pedido de Oración"
  const [showNuevoModal, setShowNuevoModal] = useState(false);
  const [nuevoTexto, setNuevoTexto] = useState('');
  const [nuevoPublico, setNuevoPublico] = useState(true);
  const [enviandoNuevo, setEnviandoNuevo] = useState(false);

  useEffect(() => {
    fetchPedidos();
  }, []);

  const crearPedido = async (e) => {
    e.preventDefault();
    if (!nuevoTexto.trim()) {
      toast.error('Escribí tu pedido de oración');
      return;
    }
    setEnviandoNuevo(true);
    try {
      await axios.post(`${API}/api/public/oracion`, {
        nombre: user?.nombre ? `${user.nombre} ${user.apellido || ''}`.trim() : 'Hermano/a en Cristo',
        email: user?.email || '',
        pedido: nuevoTexto.trim(),
        publico: nuevoPublico,
      });
      toast.success('🙏 Tu pedido fue enviado');
      setNuevoTexto('');
      setNuevoPublico(true);
      setShowNuevoModal(false);
      fetchPedidos();
    } catch (err) {
      console.error('Error creando pedido:', err);
      toast.error('No se pudo enviar el pedido. Intentá de nuevo.');
    } finally {
      setEnviandoNuevo(false);
    }
  };

  const fetchPedidos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/pedidos-oracion`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Filtrar solo pedidos públicos
      const pedidosPublicos = response.data.filter(p => p.publico === true);
      
      // Para cada pedido, obtener cuántos están orando
      const pedidosConInfo = await Promise.all(
        pedidosPublicos.map(async (pedido) => {
          try {
            const orandoRes = await axios.get(`${API}/api/pedidos-oracion/${pedido.id}/orando`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
            return {
              ...pedido,
              total_orando: orandoRes.data.total || 0,
              usuarios_orando: orandoRes.data.usuarios || [],
              estoy_orando: orandoRes.data.estoy_orando || false
            };
          } catch (err) {
            console.error('Error fetching orando info:', err);
            return {
              ...pedido,
              total_orando: 0,
              usuarios_orando: [],
              estoy_orando: false
            };
          }
        })
      );
      
      setPedidos(pedidosConInfo);
      setError('');
    } catch (err) {
      console.error('Error fetching pedidos:', err);
      setError('Error al cargar pedidos de oración');
    } finally {
      setLoading(false);
    }
  };

  const handleEstoyOrando = async (pedidoId) => {
    const pedido = pedidos.find(p => p.id === pedidoId);
    if (!pedido) return;

    if (pedido.estoy_orando) {
      // Dejar de orar (directo, sin modal)
      try {
        await axios.delete(`${API}/api/pedidos-oracion/${pedidoId}/orar`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Has dejado de orar por este pedido');
        await fetchPedidos();
      } catch (error) {
        console.error('Error dejando de orar:', error);
        toast.error('Error al actualizar estado de oración');
      }
    } else {
      // Abrir modal para dejar un mensaje opcional antes de confirmar
      setPedidoOrando(pedido);
      setMensajeOracion('');
      setShowOrandoModal(true);
    }
  };

  const confirmarOrando = async () => {
    if (!pedidoOrando) return;
    setEnviandoOracion(true);
    try {
      const mensaje = (mensajeOracion || '').trim();
      await axios.post(
        `${API}/api/pedidos-oracion/${pedidoOrando.id}/orar`,
        mensaje ? { mensaje } : {},
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('¡Gracias por orar! 🙏');
      setShowOrandoModal(false);
      setPedidoOrando(null);
      setMensajeOracion('');
      await fetchPedidos();
    } catch (error) {
      console.error('Error marcando oración:', error);
      toast.error('Error al confirmar la oración');
    } finally {
      setEnviandoOracion(false);
    }
  };

  const abrirModalOrando = async (pedido) => {
    setModalOrando(pedido);
    setLoadingModal(true);
    
    try {
      const response = await axios.get(`${API}/api/pedidos-oracion/${pedido.id}/orando`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setUsuariosOrando(response.data.usuarios || []);
    } catch (error) {
      console.error('Error cargando usuarios orando:', error);
      toast.error('Error al cargar lista de oradores');
    } finally {
      setLoadingModal(false);
    }
  };

  const cerrarModal = () => {
    setModalOrando(null);
    setUsuariosOrando([]);
  };

  const handleEliminarPedido = async (pedidoId, nombrePedido) => {
    const confirmar = window.confirm(
      `¿Estás seguro de que deseas eliminar este pedido de oración?\n\n` +
      `De: ${nombrePedido || 'Anónimo'}\n\n` +
      `Esta acción NO se puede deshacer.`
    );

    if (!confirmar) return;

    setDeleting(pedidoId);
    try {
      await axios.delete(`${API}/api/admin/oracion/${pedidoId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast.success('Pedido de oración eliminado exitosamente');
      
      // Recargar pedidos
      await fetchPedidos();
    } catch (error) {
      console.error('Error eliminando pedido:', error);
      const errorMsg = error.response?.data?.detail || 'Error al eliminar pedido de oración';
      toast.error(errorMsg);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando pedidos de oración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
              <i className="fas fa-praying-hands text-white text-2xl"></i>
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">
                Red de Oración IBA
              </h1>
              <p className="text-gray-600 mt-1">
                Intercedamos juntos en oración
              </p>
            </div>
          </div>
          <Button
            onClick={() => setShowNuevoModal(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg font-semibold"
            data-testid="btn-nuevo-pedido-oracion"
          >
            <i className="fas fa-plus mr-2"></i>
            Nuevo Pedido
          </Button>
        </div>

        {/* Info Card */}
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <i className="fas fa-info-circle text-purple-600 text-xl mt-1"></i>
              <div>
                <p className="text-sm text-purple-900 font-medium">
                  "Confesaos vuestras ofensas unos a otros, y orad unos por otros, para que seáis sanados. 
                  La oración eficaz del justo puede mucho." - Santiago 5:16
                </p>
                <p className="text-xs text-purple-700 mt-2">
                  Estos son pedidos de oración compartidos por nuestra comunidad. Oremos juntos por cada necesidad.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-800">
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pedidos de Oración */}
      {pedidos.length === 0 ? (
        <Card className="border-gray-200">
          <CardContent className="p-12 text-center">
            <div className="text-gray-400 mb-4">
              <i className="fas fa-praying-hands text-6xl"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No hay pedidos de oración públicos
            </h3>
            <p className="text-gray-500">
              Pronto se compartirán nuevas peticiones para interceder en oración
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {pedidos.map((pedido, index) => (
            <Card 
              key={pedido.id || index} 
              className="border-purple-100 hover:shadow-lg transition-all duration-300 hover:border-purple-300"
            >
              <CardHeader className="border-b border-purple-50 bg-gradient-to-r from-purple-50 to-transparent pb-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <i className="fas fa-user-circle text-purple-600"></i>
                      {pedido.nombre || 'Anónimo'}
                    </CardTitle>
                    {pedido.email && (
                      <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                        <i className="fas fa-envelope text-xs"></i>
                        {pedido.email}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      <i className="fas fa-globe-americas mr-1"></i>
                      Público
                    </div>
                    {user?.rol === 'admin' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                        onClick={() => handleEliminarPedido(pedido.id, pedido.nombre)}
                        disabled={deleting === pedido.id}
                        title="Eliminar pedido"
                      >
                        {deleting === pedido.id ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fas fa-trash-alt"></i>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="p-6">
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <i className="fas fa-hands-praying text-purple-500"></i>
                    Petición
                  </h4>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {pedido.pedido}
                  </p>
                </div>

                {pedido.fecha_creacion && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 pt-3 border-t border-gray-100">
                    <i className="fas fa-calendar"></i>
                    <span>
                      {new Date(pedido.fecha_creacion).toLocaleDateString('es-UY', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                )}

                {/* Sección de Oración */}
                <div className="mt-4 pt-4 border-t border-purple-50">
                  {/* Botón principal */}
                  <Button
                    variant="ghost"
                    className={'w-full flex items-center justify-center gap-2 ' + (pedido.estoy_orando 
                        ? 'text-green-600 hover:text-green-700 hover:bg-green-50 bg-green-50' 
                        : 'text-purple-600 hover:text-purple-700 hover:bg-purple-50')}
                    onClick={() => handleEstoyOrando(pedido.id)}
                  >
                    <i className={'fas ' + (pedido.estoy_orando ? 'fa-check-circle' : 'fa-praying-hands')}></i>
                    <span className="font-medium">
                      {pedido.estoy_orando ? '✓ Estás Orando' : 'Estoy Orando'}
                    </span>
                    {pedido.total_orando > 0 && (
                      <span className="ml-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                        {pedido.total_orando}
                      </span>
                    )}
                  </Button>

                  {/* Fotos de perfil y "Ver todos" */}
                  {pedido.total_orando > 0 && (
                    <div className="mt-3 flex items-center justify-between">
                      {/* Fotos superpuestas */}
                      <div className="flex -space-x-2">
                        {pedido.usuarios_orando.slice(0, 5).map((usuario, idx) => (
                          <div
                            key={usuario.id || idx}
                            className="w-8 h-8 rounded-full bg-purple-100 border-2 border-white flex items-center justify-center overflow-hidden"
                            title={usuario.nombre}
                          >
                            {usuario.foto_perfil ? (
                              <img 
                                src={usuario.foto_perfil} 
                                alt={usuario.nombre}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-xs font-semibold text-purple-600">
                                {usuario.nombre.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                        ))}
                        {pedido.total_orando > 5 && (
                          <div className="w-8 h-8 rounded-full bg-purple-200 border-2 border-white flex items-center justify-center">
                            <span className="text-xs font-semibold text-purple-700">
                              +{pedido.total_orando - 5}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Botón Ver todos */}
                      <button
                        onClick={() => abrirModalOrando(pedido)}
                        className="text-xs text-purple-600 hover:text-purple-700 font-medium hover:underline"
                      >
                        Ver todos →
                      </button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Stats Footer */}
      {pedidos.length > 0 && (
        <Card className="mt-8 border-purple-100 bg-gradient-to-r from-purple-50 to-transparent">
          <CardContent className="p-6">
            <div className="flex items-center justify-center gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-purple-600">
                  {pedidos.length}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Peticiones Activas
                </div>
              </div>
              <div className="h-12 w-px bg-purple-200"></div>
              <div>
                <div className="text-3xl font-bold text-purple-600">
                  {pedidos.reduce((sum, p) => sum + (p.total_orando || 0), 0)}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Personas Orando
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de Usuarios Orando */}
      {modalOrando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Header del Modal */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <i className="fas fa-praying-hands text-purple-600"></i>
                    Personas Orando
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {modalOrando.total_orando} {modalOrando.total_orando === 1 ? 'persona está' : 'personas están'} orando por esta petición
                  </p>
                </div>
                <button
                  onClick={cerrarModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fas fa-times text-2xl"></i>
                </button>
              </div>
            </div>

            {/* Contenido del Modal */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingModal ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : usuariosOrando.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <i className="fas fa-users text-4xl mb-3 text-gray-300"></i>
                  <p>Aún no hay personas orando</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {usuariosOrando.map((usuario, idx) => (
                    <div
                      key={usuario.id || idx}
                      className="flex items-center gap-4 p-4 rounded-lg hover:bg-purple-50 transition-colors"
                    >
                      {/* Foto/Avatar */}
                      <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {usuario.foto_perfil ? (
                          <img 
                            src={usuario.foto_perfil} 
                            alt={usuario.nombre}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-lg font-semibold text-purple-600">
                            {usuario.nombre.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate">
                          {usuario.nombre}
                        </p>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <i className="fas fa-clock text-xs"></i>
                          <span>
                            Orando desde {new Date(usuario.fecha_inicio_oracion).toLocaleDateString('es-UY', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                        </p>
                        {usuario.mensaje && (
                          <div className="mt-2 bg-purple-50 border-l-4 border-purple-400 px-3 py-2 rounded">
                            <p className="text-sm text-purple-900 italic" data-testid="mensaje-orador">
                              <i className="fas fa-quote-left text-xs text-purple-400 mr-1"></i>
                              {usuario.mensaje}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Badge */}
                      <div className="flex-shrink-0">
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          <i className="fas fa-praying-hands mr-1"></i>
                          Orando
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer del Modal */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <Button
                onClick={cerrarModal}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal "Estoy Orando" con mensaje opcional */}
      {showOrandoModal && pedidoOrando && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="modal-orando">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-white">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <i className="fas fa-praying-hands"></i>
                Voy a orar por este pedido
              </h3>
              <p className="text-xs text-white/85 mt-1">Podés dejar un mensaje breve de aliento (opcional)</p>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-xs text-purple-700 font-semibold mb-1">Pedido de:</p>
                <p className="text-sm text-purple-900 font-medium">{pedidoOrando.nombre || 'Hermano/a en Cristo'}</p>
                <p className="text-sm text-gray-700 mt-2 line-clamp-3">{pedidoOrando.pedido}</p>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">
                  Mensaje o versículo <span className="text-gray-400 font-normal">(opcional, 300 caracteres)</span>
                </label>
                <textarea
                  value={mensajeOracion}
                  onChange={(e) => setMensajeOracion(e.target.value.slice(0, 300))}
                  placeholder='Ej: "Fuerza hermano, estoy contigo en oración" o "Salmo 34:17: Claman los justos y el Señor los oye..."'
                  rows={4}
                  maxLength={300}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-purple-500 focus:outline-none resize-none"
                  data-testid="textarea-mensaje-oracion"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{mensajeOracion.length}/300</p>
              </div>

              <p className="text-[11px] text-gray-500 italic">
                Al confirmar, la persona que hizo el pedido y todos los que ven la Red podrán ver tu mensaje.
              </p>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  onClick={() => { setShowOrandoModal(false); setPedidoOrando(null); setMensajeOracion(''); }}
                  disabled={enviandoOracion}
                  variant="outline"
                  data-testid="btn-cancelar-orando"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={confirmarOrando}
                  disabled={enviandoOracion}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  data-testid="btn-confirmar-orando"
                >
                  {enviandoOracion ? (
                    <><i className="fas fa-spinner fa-spin mr-1"></i>Enviando...</>
                  ) : (
                    <><i className="fas fa-praying-hands mr-1"></i>Confirmar oración</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal "Nuevo Pedido de Oración" */}
      {showNuevoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="modal-nuevo-pedido">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-white">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <i className="fas fa-praying-hands"></i>
                Nuevo Pedido de Oración
              </h3>
              <p className="text-xs text-white/85 mt-1">Compartí tu petición con la red de intercesión</p>
            </div>
            <form onSubmit={crearPedido} className="p-4 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1 block">
                  Tu pedido de oración
                </label>
                <textarea
                  value={nuevoTexto}
                  onChange={(e) => setNuevoTexto(e.target.value)}
                  placeholder="Escribí aquí tu pedido..."
                  rows={5}
                  maxLength={1000}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-purple-500 focus:outline-none resize-none"
                  data-testid="input-pedido-texto"
                  required
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{nuevoTexto.length}/1000</p>
              </div>

              <label className="flex items-start gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors">
                <input
                  type="checkbox"
                  checked={nuevoPublico}
                  onChange={(e) => setNuevoPublico(e.target.checked)}
                  className="w-4 h-4 accent-purple-600 mt-0.5"
                  data-testid="input-pedido-publico"
                />
                <div className="text-sm">
                  <div className="font-semibold text-purple-900">
                    {nuevoPublico ? '🌐 Público' : '🔒 Privado'}
                  </div>
                  <div className="text-xs text-purple-700">
                    {nuevoPublico
                      ? 'Todos los miembros de la Red de Oración lo verán y podrán orar por vos'
                      : 'Solo el equipo pastoral lo leerá, no aparecerá en la red pública'}
                  </div>
                </div>
              </label>

              <p className="text-[11px] text-gray-500 italic">
                Se enviará a nombre de <strong>{user?.nombre || 'vos'}</strong>. 
                {nuevoPublico && ' Cuando un hermano comience a orar, vas a recibir una notificación.'}
              </p>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  onClick={() => setShowNuevoModal(false)}
                  disabled={enviandoNuevo}
                  variant="outline"
                  data-testid="btn-cancelar-pedido"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={enviandoNuevo || !nuevoTexto.trim()}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  data-testid="btn-enviar-pedido"
                >
                  {enviandoNuevo ? (
                    <><i className="fas fa-spinner fa-spin mr-1"></i>Enviando...</>
                  ) : (
                    <><i className="fas fa-paper-plane mr-1"></i>Enviar pedido</>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedOracionIBA;
